import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { api, type User } from "@shared/routes";
import { z } from "zod";
import { useLocation } from "wouter";

export function useAuth() {
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  const { data: user, isLoading, isError } = useQuery({
    queryKey: [api.auth.me.path],
    queryFn: async () => {
      const res = await fetch(api.auth.me.path);
      if (res.status === 401) return null;
      if (!res.ok) throw new Error("Failed to fetch user");
      return api.auth.me.responses[200].parse(await res.json());
    },
    retry: false,
  });

  // Keep Sentry user context in sync with auth state
  useEffect(() => {
    if (user) {
      const u = user as any;
      Sentry.setUser({ id: String(u.id), username: u.username });
      Sentry.setTag("role", u.role ?? "");
      Sentry.setTag("organizationId", String(u.organizationId ?? ""));
    } else if (!isLoading) {
      Sentry.setUser(null);
    }
  }, [user, isLoading]);

  const loginMutation = useMutation({
    mutationFn: async (credentials: z.infer<typeof api.auth.login.input>) => {
      const res = await fetch(api.auth.login.path, {
        method: api.auth.login.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
      });
      if (!res.ok) {
        throw new Error("Invalid credentials");
      }
      return api.auth.login.responses[200].parse(await res.json());
    },
    onSuccess: (data) => {
      queryClient.setQueryData([api.auth.me.path], data);
      const role = (data as any).role || 'super_admin';
      if (role === 'platform_admin') {
        setLocation("/platform");
      } else if (role === 'cleaner') {
        setLocation("/cleaner");
      } else if (role === 'maintenance') {
        setLocation("/maintenance");
      } else if (role === 'owner') {
        setLocation("/owner");
      } else if (role === 'affiliate') {
        setLocation("/affiliate");
      } else {
        setLocation("/admin");
      }
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await fetch(api.auth.logout.path, { method: api.auth.logout.method, credentials: "same-origin" });
    },
    onSuccess: () => {
      queryClient.clear();
      setLocation("/auth");
    },
  });

  return {
    user,
    isLoading,
    isError,
    login: loginMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
  };
}
